import React, { forwardRef, useEffect, useMemo, useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slide,
  Typography,
  Button,
  Box,
  Divider,
  Stack,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  TextField,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Cancel } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { adminModulesConfigForRole } from '../../util/constants';

import {
  createNewRole,
  editRole,
  closeRoleDialog,
} from '../../store/role/action';
import { Toast } from '../../util/Toast';

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ACTIONS = ['List', 'Create', 'Edit', 'Delete'];

const emptyRow = () => ({ List: false, Create: false, Edit: false, Delete: false });

const RoleDialog = () => {
  const dispatch = useDispatch();
  const { dialog: open, dialogData: role } = useSelector((state) => state.role);

  const { uniqueModules, sections } = adminModulesConfigForRole;

  const initialPerms = useMemo(() => {
    const base = {};
    uniqueModules.forEach((m) => {
      base[m.key] = emptyRow();
    });
    if (role?.permissions?.length) {
      for (const p of role.permissions) {
        const modKey = p.module;
        const row = emptyRow();
        (p.actions || []).forEach((a) => {
          const match = ACTIONS.find(
            (x) => x.toLowerCase() === String(a).toLowerCase()
          );
          if (match) row[match] = true;
        });
        if (base[modKey]) {
          base[modKey] = row;
        }
      }
    }
    return base;
  }, [role, uniqueModules]);

  const [name, setName] = useState(role?.name || '');
  const [errors, setErrors] = useState({ name: '' });
  const [perms, setPerms] = useState(initialPerms);
  const [initialState, setInitialState] = useState(null);

  useEffect(() => {
    if (!open) return;
    setName(role?.name || '');
    setPerms(initialPerms);
    setInitialState({
      name: role?.name || '',
      permissions: toPermissionsArray(initialPerms),
    });
  }, [open, role?._id, initialPerms]);

  const enabledCount = useMemo(
    () =>
      Object.values(perms).filter((p) =>
        Object.values(p).some(Boolean)
      ).length,
    [perms]
  );

  const validateName = (v) => {
    const s = (v || '').trim();
    if (!s) return 'Role name is required';
    if (s.length < 3) return 'Minimum 3 characters';
    if (s.length > 50) return 'Maximum 50 characters';
    return undefined;
  };

  const toggleAction = useCallback((key, action) => {
    setPerms((prev) => ({
      ...prev,
      [key]: { ...prev[key], [action]: !prev[key][action] },
    }));
  }, []);

  const toggleRowAll = useCallback((key, value) => {
    setPerms((prev) => ({
      ...prev,
      [key]: ACTIONS.reduce((acc, a) => {
        acc[a] = !!value;
        return acc;
      }, {}),
    }));
  }, []);

  const getRowAllState = useCallback(
    (key) => {
      const row = perms[key] || emptyRow();
      const values = Object.values(row);
      const all = values.every(Boolean);
      const none = values.every((v) => !v);
      return { all, indeterminate: !all && !none };
    },
    [perms]
  );

  const sectionRows = useMemo(() => {
    const map = {};
    sections.forEach((section) => {
      map[section] = uniqueModules
        .filter((m) => m.section === section)
        .map((m) => m.key);
    });
    return map;
  }, [sections, uniqueModules]);

  const getSectionAllState = useCallback(
    (section) => {
      const keys = sectionRows[section] || [];
      if (!keys.length) return { all: false, indeterminate: false };
      const allRowsAll = keys.every((k) =>
        Object.values(perms[k] || {}).every(Boolean)
      );
      const allRowsNone = keys.every((k) =>
        Object.values(perms[k] || {}).every((v) => !v)
      );
      return {
        all: allRowsAll,
        indeterminate: !allRowsAll && !allRowsNone,
      };
    },
    [sectionRows, perms]
  );

  const setSectionAll = useCallback(
    (section, value) => {
      const keys = sectionRows[section] || [];
      setPerms((prev) => {
        const next = { ...prev };
        keys.forEach((k) => {
          next[k] = ACTIONS.reduce((acc, a) => {
            acc[a] = !!value;
            return acc;
          }, {});
        });
        return next;
      });
    },
    [sectionRows]
  );

  const toPermissionsArray = useCallback((state) => {
    return Object.entries(state)
      .map(([moduleKey, row]) => {
        const actions = ACTIONS.filter((a) => row[a]);
        if (!actions.length) return null;
        return { module: moduleKey, actions };
      })
      .filter(Boolean);
  }, []);

  const handleSave = () => {
    const nameErr = validateName(name);
    setErrors({ name: nameErr || '' });
    if (nameErr) return;

    const permissions = toPermissionsArray(perms).map(p => ({
      module: p.module,
      actions: [...(p.actions || [])].sort()
    })).sort((a, b) => a.module.localeCompare(b.module));

    const originalPermsClean = (role?.permissions || []).map(p => ({
      module: p.module,
      actions: [...(p.actions || [])].sort()
    })).sort((a, b) => a.module.localeCompare(b.module));

    if (
      role?._id &&
      name.trim() === (role?.name || "") &&
      JSON.stringify(permissions) === JSON.stringify(originalPermsClean)
    ) {
      Toast("info", "No changes detected");
      dispatch(closeRoleDialog());
      return;
    }

    if (role?._id) {
      dispatch(editRole({ roleId: role._id, name: name.trim(), permissions }));
    } else {
      dispatch(createNewRole({ name: name.trim(), permissions }));
    }
  };

  const handleClose = () => {
    dispatch(closeRoleDialog());
    setErrors({ name: '' });
  };

  const mode = role?._id ? 'Edit' : 'Create';

  const darkPaperSx = {
    overflow: 'visible',
    width: '650px',
    maxWidth: '95vw',
    backgroundColor: '#000000',
    color: '#343a40',
  };

  const darkTableSx = {
    backgroundColor: '#e9ecef',
    '& .MuiTableCell-head': {
      backgroundColor: '#f1f3f5',
      color: '#343a40',
      fontWeight: 600,
      borderColor: '#f1f3f5',
    },
    '& .MuiTableCell-body': {
      color: '#343a40',
      borderColor: '#f1f3f5',
    },
    '& .MuiTableRow-hover:hover': {
      backgroundColor: '#f1f3f5',
    },
  };

  return (
    <Dialog
      open={open}
      keepMounted
      scroll="paper"
      onClose={handleClose}
      TransitionComponent={Transition}
      fullWidth
      maxWidth="lg"
      PaperProps={{ sx: darkPaperSx }}
    >
      <DialogTitle sx={{ pr: 10, color: '#343a40' }}>
        <Typography variant="h5" component="span" sx={{ color: '#343a40' }}>
          {mode} Role
        </Typography>
        <Typography variant="body1" component="div" className="mt-2" sx={{ color: '#343a40' }}>
          This role has access to {enabledCount} module
          {enabledCount === 1 ? '' : 's'} with various permission levels.
        </Typography>
        <Tooltip title="Close">
          <IconButton
            onClick={handleClose}
            sx={{ position: 'absolute', right: 8, top: 8, color: '#FFD700' }}
            size="small"
          >
            <Cancel />
          </IconButton>
        </Tooltip>
      </DialogTitle>

      <DialogContent sx={{ pt: 0, color: '#343a40' }}>
        <Box sx={{ mb: 3, mt: 3 }}>
          <TextField
            fullWidth
            label="Role Name"
            placeholder="Enter Role Name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (errors.name) setErrors((prev) => ({ ...prev, name: '' }));
            }}
            error={!!errors.name}
            helperText={errors.name || ' '}
            sx={{
              '& .MuiInputBase-root': { color: '#343a40' },
              '& .MuiInputLabel-root': { color: '#343a40' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#f1f3f5' },
              '& .MuiFormHelperText-root': { color: '#343a40' },
              '& .MuiInputLabel-root.Mui-focused': { color: '#FFD700' },
              '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#FFD700' },
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#FFD700' },
            }}
          />
        </Box>

        <Divider sx={{ mb: 3, borderColor: '#f1f3f5' }} />

        <Stack spacing={4}>
          {sections.map((section) => {
            const rowsInSection = uniqueModules.filter(
              (m) => m.section === section
            );
            if (!rowsInSection.length) return null;
            const { all, indeterminate } = getSectionAllState(section);

            return (
              <Box key={section}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 1.5, gap: 2, flexWrap: 'wrap' }}
                >
                  <Typography variant="h6" sx={{ color: '#343a40' }}>{section}</Typography>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Typography variant="body2" sx={{ color: '#343a40' }}>
                      Grant all in section
                    </Typography>
                    <Checkbox
                      checked={all}
                      indeterminate={indeterminate}
                      onChange={(_, v) => setSectionAll(section, v)}
                      inputProps={{
                        'aria-label': `Grant all in ${section}`,
                      }}
                      sx={{
                        color: '#343a40',
                        '&.Mui-checked': { color: '#FFD700' },
                        '&.MuiCheckbox-indeterminate': { color: '#FFD700' },
                      }}
                    />
                  </Stack>
                </Stack>

                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    backgroundColor: '#e9ecef',
                    borderColor: '#f1f3f5',
                    ...darkTableSx,
                  }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, width: 320 }}>
                          Module
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600, width: 90 }}>
                          All
                        </TableCell>
                        {ACTIONS.map((a) => (
                          <TableCell key={a} align="center" sx={{ fontWeight: 600 }}>
                            {a}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rowsInSection.map((m) => {
                        const row = perms[m.key] || emptyRow();
                        const {
                          all: rowAll,
                          indeterminate: rowInd,
                        } = getRowAllState(m.key);
                        return (
                          <TableRow key={m.key} hover>
                            <TableCell>
                              <Stack direction="row" alignItems="center" spacing={1.5}>
                                <Box component="span" sx={{ display: 'flex' }}>
                                  {m.icon}
                                </Box>
                                <Box>
                                  <Typography fontWeight={600} sx={{ color: '#343a40' }}>
                                    {m.name}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: '#6c757d' }}>
                                    {m.key}
                                  </Typography>
                                </Box>
                              </Stack>
                            </TableCell>
                            <TableCell align="center">
                              <Checkbox
                                checked={rowAll}
                                indeterminate={rowInd}
                                onChange={(_, v) => toggleRowAll(m.key, v)}
                                inputProps={{
                                  'aria-label': `Select all for ${m.name}`,
                                }}
                                sx={{
                                  color: '#343a40',
                                  '&.Mui-checked': { color: '#FFD700' },
                                  '&.MuiCheckbox-indeterminate': { color: '#FFD700' },
                                }}
                              />
                            </TableCell>
                            {ACTIONS.map((a) => (
                              <TableCell key={a} align="center">
                                <Checkbox
                                  checked={!!row[a]}
                                  onChange={() => toggleAction(m.key, a)}
                                  inputProps={{
                                    'aria-label': `${a} for ${m.name}`,
                                  }}
                                  sx={{
                                    color: '#343a40',
                                    '&.Mui-checked': { color: '#FFD700' },
                                  }}
                                />
                              </TableCell>
                            ))}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            );
          })}
        </Stack>
      </DialogContent>

      <DialogActions className="p-3" sx={{ borderTop: '1px solid #f1f3f5' }}>
        <Button
          variant="outlined"
          onClick={handleClose}
          sx={{
            color: '#343a40',
            borderColor: '#f1f3f5',
            '&:hover': { borderColor: '#FFD700', color: '#FFD700' },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          sx={{
            backgroundColor: '#FFD700',
            '&:hover': { backgroundColor: '#d9457a' },
          }}
        >
          {mode === 'Edit' ? 'Update Role' : 'Create Role'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RoleDialog;
